import React, { useEffect, useState, useCallback } from 'react';
import { get, post } from '../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function money(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value || 0));
}
function number(value) {
  return new Intl.NumberFormat('en-US').format(Number(value || 0));
}
function revenuePerLead(revenue, enrolled) {
  const leadCount = Number(enrolled || 0);
  if (!leadCount) return 0;
  return Number(revenue || 0) / leadCount;
}

function pct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(1)}%`;
}

function shortDate(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function triggerDisplay(row) {
  const labels = {
    goal_answer_followup: 'Instant answer follow-up',
    frustration_answer_followup: 'Instant frustration follow-up',
    hot_lead_coi: '24h offer follow-up',
    hot_lead_invest: '48h offer follow-up',
    engaged_lurker: 'Engaged lurker reply prompt',
  };
  const timing = {
    goal_answer_followup: 'Sends immediately after an answer click',
    frustration_answer_followup: 'Sends immediately after a frustration click',
    hot_lead_coi: 'Sends 24h after offer click if they have not bought',
    hot_lead_invest: 'Sends 48h after offer click if they have not bought',
    engaged_lurker: 'Sends after high engagement without offer clicks',
  };
  return {
    label: labels[row.trigger_key] || row.subject || row.trigger_key,
    detail: timing[row.trigger_key] || `${row.trigger_key} · ${row.trigger_type}`,
  };
}

// ─── Email Preview Modal ──────────────────────────────────────────────────────
function EmailPreviewModal({ funnel, emails, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(true);

  const email = emails[index];
  const totalEmails = emails.length;

  const loadEmail = useCallback(async (idx) => {
    setLoading(true);
    try {
      const data = await get(`/api/funnels/${funnel.id}/emails/${emails[idx].id}`);
      setEmailData(data);
    } catch {
      setEmailData(null);
    } finally {
      setLoading(false);
    }
  }, [funnel.id, emails]);

  useEffect(() => { loadEmail(index); }, [index, loadEmail]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '3rem', background: 'rgba(0,0,0,0.65)' }}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 580, background: '#141414', borderRadius: 12, border: '0.5px solid #2a2a2a', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: '0.5px solid #2a2a2a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>✉️</span>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#fff' }}>Email Preview</span>
            <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: 'rgba(107,114,128,0.2)', color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase' }}>READ-ONLY</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6B7280', fontSize: 20, cursor: 'pointer', padding: 4 }}>✕</button>
        </div>

        {/* Metadata */}
        <div style={{ padding: '16px 20px', display: 'grid', gap: 8 }}>
          {[
            ['Funnel', emailData?.funnel_name || funnel.name],
            ['Step', emailData?.step_label || `${index + 1} of ${totalEmails}`],
            ['From', emailData ? `${emailData.from_name} <${emailData.from_email}>` : '—'],
            ['Subject', emailData?.subject || email?.subject || '—'],
          ].map(([label, value]) => (
            <div key={label} style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8, fontSize: 13 }}>
              <span style={{ color: '#6B7280', fontWeight: 600 }}>{label}</span>
              <span style={{ color: label === 'Subject' ? '#fff' : '#d4d4d4', fontWeight: label === 'Subject' ? 500 : 400 }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{
            background: '#0f0f0f',
            border: '0.5px solid #2a2a2a',
            borderRadius: 8,
            padding: 16,
            maxHeight: 320,
            overflowY: 'auto',
            fontSize: 14,
            lineHeight: 1.7,
            color: '#d4d4d4',
          }}>
            {loading ? (
              <div style={{ color: '#6B7280' }}>Loading…</div>
            ) : emailData?.body_html ? (
              <div dangerouslySetInnerHTML={{ __html: emailData.body_html }} />
            ) : (
              <div style={{ color: '#6B7280' }}>No content available.</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '0.5px solid #2a2a2a' }}>
          <span style={{ fontSize: 11, color: '#6B7280' }}>Editing planned for a future upgrade</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={index === 0}
              onClick={() => setIndex(i => i - 1)}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #3a3a3a', background: 'transparent', color: index === 0 ? '#3a3a3a' : '#d4d4d4', fontSize: 12, fontWeight: 600, cursor: index === 0 ? 'default' : 'pointer' }}
            >Previous</button>
            <button
              disabled={index === totalEmails - 1}
              onClick={() => setIndex(i => i + 1)}
              style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #3a3a3a', background: 'transparent', color: index === totalEmails - 1 ? '#3a3a3a' : '#d4d4d4', fontSize: 12, fontWeight: 600, cursor: index === totalEmails - 1 ? 'default' : 'pointer' }}
            >Next</button>
            <button onClick={onClose} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#374151', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Funnel Card Component ────────────────────────────────────────────────────
function FunnelCard({ funnel, onToggle, toggling, isExpanded, onExpand }) {
  const [modal, setModal] = useState(null); // index of email to preview
  const isSequence = funnel.type === 'sequence';
  const isMemberCompletion = funnel.type === 'member_completion';
  const usesEnrollmentStats = isSequence || isMemberCompletion;
  const emails = funnel.emails || [];

  const stats = funnel.stats || {};
  const attribution = funnel.attribution || null;
  const feedback = funnel.feedback || null;
  const abandonment = funnel.abandonment || null;
  const triggerEmails = feedback?.trigger_emails || [];
  const triggerRevenue = triggerEmails.reduce((sum, row) => sum + Number(row.revenue || 0), 0);
  const triggerBuyers = triggerEmails.reduce((sum, row) => sum + Number(row.buyers || 0), 0);
  const triggerInfluencedRevenue = triggerEmails.reduce((sum, row) => sum + Number(row.influenced_revenue || 0), 0);
  const triggerInfluencedBuyers = triggerEmails.reduce((sum, row) => sum + Number(row.influenced_buyers || 0), 0);
  const triggerQueued = triggerEmails.reduce((sum, row) => sum + Number(row.pending || 0) + Number(row.processing || 0), 0);
  const triggerReplies = triggerEmails.reduce((sum, row) => sum + Number(row.replies || 0), 0);
  const feedbackReplies = Math.max(Number(feedback?.totals?.replies || 0), triggerReplies);
  const recentActivity = funnel.recent_activity || [];
  const enrolled = Number(usesEnrollmentStats ? stats.enrolled : stats.opt_ins || 0);
  const active = Number(usesEnrollmentStats ? stats.active : stats.opt_ins || 0);
  const sent = Number(usesEnrollmentStats ? stats.sent : stats.recipients || 0);
  const clicks = Number(usesEnrollmentStats ? stats.clicks : stats.offer_clicks || 0);
  const buyers = Number(stats.buyers || 0);
  const revenue = Number(stats.revenue || 0);
  const rpl = revenuePerLead(revenue, enrolled);
  const buyerLabel = String(stats.buyer_label || 'Buyers').toUpperCase();
  const hasTargetBreakdown = attribution && (
    attribution.target_paid_buyers !== undefined
    || attribution.downstream_paid_buyers !== undefined
    || attribution.free_orders !== undefined
    || attribution.transactions !== undefined
  );

  // Type badge
  const typeBadge = isSequence
    ? { label: 'SEQUENCE ENGINE', bg: 'rgba(107,114,128,0.12)', color: '#9CA3AF', border: '0.5px solid rgba(107,114,128,0.3)' }
    : isMemberCompletion
      ? { label: 'MEMBER FUNNEL', bg: 'rgba(55,138,221,0.12)', color: '#378ADD', border: '0.5px solid rgba(55,138,221,0.3)' }
      : { label: 'ANALYTICS ONLY', bg: 'rgba(186,117,23,0.12)', color: '#BA7517', border: '0.5px solid rgba(186,117,23,0.3)' };

  const statusPill = funnel.status === 'active'
    ? { label: 'ACTIVE', bg: 'rgba(16,185,129,0.15)', color: '#10b981' }
    : { label: 'PAUSED', bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' };

  const statCells = [
    { label: String(stats.enrolled_label || 'Enrolled').toUpperCase(), value: number(enrolled), color: '#fff' },
    { label: String(stats.active_label || 'Active').toUpperCase(), value: number(active), color: '#378ADD' },
    { label: 'SENT', value: number(sent), color: '#fff' },
    { label: 'CLICKS', value: number(clicks), color: '#378ADD' },
    { label: buyerLabel, value: number(buyers), color: '#1D9E75' },
    { label: 'REVENUE', value: money(revenue), color: '#1D9E75' },
    { label: 'REVENUE / LEAD', value: money(rpl), color: '#1D9E75' },
  ];

  return (
    <>
      <div
        onClick={onExpand}
        style={{ background: '#141414', borderRadius: 12, border: '0.5px solid #2a2a2a', overflow: 'hidden', cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#3a3a3a'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ color: '#6B7280', fontSize: 14, transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>›</span>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 500, color: '#fff' }}>{funnel.name}</h3>
              <span style={{ padding: '3px 10px', borderRadius: 999, background: statusPill.bg, color: statusPill.color, fontWeight: 800, fontSize: 10, textTransform: 'uppercase' }}>{statusPill.label}</span>
              <span style={{ padding: '3px 10px', borderRadius: 4, background: typeBadge.bg, color: typeBadge.color, border: typeBadge.border, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{typeBadge.label}</span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#6B7280', maxWidth: 600 }}>{funnel.description}</p>
            {funnel.meta && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#4B5563' }}>
                {isSequence ? (
                  <>Trigger: {funnel.meta.trigger} · Slug: <code style={{ fontSize: 11 }}>{funnel.meta.slug}</code></>
                ) : isMemberCompletion ? (
                  <>Trigger: {funnel.meta.trigger}</>
                ) : (
                  <>Offers: {funnel.meta.offers?.join(' · ')}</>
                )}
              </div>
            )}
          </div>
          {isSequence && (
            <button
              onClick={(e) => { e.stopPropagation(); onToggle(funnel); }}
              disabled={toggling}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: `1px solid ${funnel.status === 'active' ? '#f59e0b' : '#10b981'}`,
                background: 'transparent',
                color: funnel.status === 'active' ? '#f59e0b' : '#10b981',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {funnel.status === 'active' ? 'Pause' : 'Resume'}
            </button>
          )}
        </div>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderTop: '0.5px solid #2a2a2a', padding: '14px 20px', gap: 8 }}>
          {statCells.map(cell => (
            <div key={cell.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 4 }}>{cell.label}</div>
              <div style={{ fontSize: 16, fontWeight: 500, color: cell.color }}>{cell.value}</div>
            </div>
          ))}
        </div>

        {isExpanded && attribution && (
          <div onClick={e => e.stopPropagation()} style={{ borderTop: '0.5px solid #2a2a2a', padding: '14px 20px', cursor: 'default' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#4B5563', marginBottom: 10 }}>
              LANDING PAGE ATTRIBUTION
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: hasTargetBreakdown ? 'repeat(4, 1fr)' : 'repeat(6, 1fr)', gap: 10 }}>
              {(hasTargetBreakdown ? [
                ['PAGE VIEWS', number(attribution.page_views), '#fff'],
                ['OPT-INS', number(attribution.optins), '#fff'],
                ['OPT-IN RATE', attribution.optin_rate === null || attribution.optin_rate === undefined ? '—' : `${Number(attribution.optin_rate).toFixed(2)}%`, '#10b981'],
                ['PAID STRATEGY ORDERS', `${number(attribution.target_paid_buyers)} / ${money(attribution.target_paid_revenue)}`, '#1D9E75'],
                ['UNIQUE STRATEGY BUYERS', number(attribution.target_paid_unique_buyers), '#1D9E75'],
                ['DOWNSTREAM PAID', `${number(attribution.downstream_paid_buyers)} / ${money(attribution.downstream_paid_revenue)}`, '#1D9E75'],
                ['FREE / ZERO ROWS', number(attribution.free_orders), '#9CA3AF'],
                ['ALL ATTRIBUTED TXNS', number(attribution.transactions), '#9CA3AF'],
              ] : [
                ['PAGE VIEWS', number(attribution.page_views), '#fff'],
                ['OPT-INS', number(attribution.optins), '#fff'],
                ['OPT-IN RATE', attribution.optin_rate === null || attribution.optin_rate === undefined ? '—' : `${Number(attribution.optin_rate).toFixed(2)}%`, '#10b981'],
                ['BUYERS', number(attribution.buyers), '#1D9E75'],
                ['REVENUE', money(attribution.revenue), '#1D9E75'],
                ['TOP SOURCE', attribution.sources?.[0] ? `${attribution.sources[0].source} (${number(attribution.sources[0].optins)})` : '—', '#9CA3AF'],
              ]).map(([label, value, color]) => (
                <div key={label} style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color }}>{value}</div>
                </div>
              ))}
            </div>
            {hasTargetBreakdown && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#6B7280', lineHeight: 1.5 }}>
                Card buyers count paid Strategy purchases only. Revenue includes all paid downstream purchases attributed to this funnel; free or zero-dollar transaction rows are listed separately.
              </div>
            )}
            {hasTargetBreakdown && (
              <div style={{ marginTop: 12, background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 10 }}>Offer-page stage tracking</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                  {[
                    ['OFFER VIEWS', number(attribution.offer_views), '#fff'],
                    ['OFFER CTA CLICKS', number(attribution.offer_cta_clicks), '#378ADD'],
                    ['CHECKOUT CLICKS', number(attribution.checkout_clicks), '#1D9E75'],
                  ].map(([label, value, color]) => (
                    <div key={label} style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 6, padding: '9px 10px' }}>
                      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color }}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {attribution.sources?.length > 1 && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#6B7280' }}>
                Sources: {attribution.sources.map(s => `${s.source}/${s.medium}/${s.campaign}/${s.content}${s.sequence_slug ? ` (${s.sequence_slug})` : ''}: ${number(s.optins)} opt-ins${Number(s.revenue || 0) > 0 ? `, ${money(s.revenue)}` : ''}`).join(' · ')}
              </div>
            )}
          </div>
        )}

        {isExpanded && feedback && (
          <div onClick={e => e.stopPropagation()} style={{ borderTop: '0.5px solid #2a2a2a', padding: '14px 20px', cursor: 'default' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#4B5563' }}>
                FEEDBACK LOOP — LIVE
              </div>
              <div style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>● realtime</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10, marginBottom: 12 }}>
              {[
                ['ANSWER CLICKS', number(feedback.totals?.answer_clicks), '#378ADD'],
                ['REPLIES', number(feedbackReplies), feedbackReplies > 0 ? '#8B5CF6' : '#4B5563'],
                ['TRIGGERS SENT', number(feedback.totals?.triggers_sent), '#1D9E75'],
                ['QUEUED', number(triggerQueued), triggerQueued > 0 ? '#f59e0b' : '#6B7280'],
                ['PURCHASE EVENTS', number(feedback.totals?.purchases), '#1D9E75'],
                ['CLICK REVENUE', `${money(triggerRevenue)} · ${number(triggerBuyers)} buyers`, triggerRevenue > 0 ? '#1D9E75' : '#4B5563'],
                ['7D INFLUENCE', `${money(triggerInfluencedRevenue)} · ${number(triggerInfluencedBuyers)} buyers`, triggerInfluencedRevenue > 0 ? '#1D9E75' : '#4B5563'],
              ].map(([label, value, color]) => (
                <div key={label} style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280' }}>Trigger emails</div>
                <div style={{ fontSize: 11, color: '#4B5563' }}>Click revenue is strict. 7d influence credits the latest feedback email sent before purchase, including reply prompts with no links.</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {triggerEmails.map(row => {
                  const display = triggerDisplay(row);
                  const queued = Number(row.pending || 0) + Number(row.processing || 0);
                  const engagement = row.trigger_key === 'engaged_lurker'
                    ? `Replies: ${number(row.replies)} · Reply rate: ${pct(row.reply_rate)}`
                    : `Clicks: ${number(row.clicks)} · Click rate: ${pct(row.click_rate)}`;
                  return (
                    <div
                      key={row.trigger_key}
                        style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) 74px 74px 72px minmax(0, 1fr) 88px 100px 106px', gap: 10, alignItems: 'center', padding: '9px 10px', background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 6 }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#fff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display.label}</div>
                        <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display.detail} · “{row.subject || row.trigger_key}”</div>
                      </div>
                      <div style={{ fontSize: 11, color: '#d4d4d4' }}>Sent: {number(row.sent)}</div>
                      <div style={{ fontSize: 11, color: queued > 0 ? '#f59e0b' : '#6B7280' }}>Queued: {number(queued)}</div>
                      <div style={{ fontSize: 11, color: Number(row.errors || 0) > 0 ? '#EF4444' : '#6B7280' }}>Errors: {number(row.errors)}</div>
                      <div style={{ fontSize: 11, color: Number(row.replies || 0) > 0 ? '#8B5CF6' : '#378ADD', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{engagement}</div>
                      <div style={{ fontSize: 11, color: Number(row.buyers || 0) > 0 ? '#1D9E75' : '#4B5563' }}>Click buyers: {number(row.buyers)}</div>
                      <div style={{ fontSize: 11, color: Number(row.revenue || 0) > 0 ? '#1D9E75' : '#4B5563', fontWeight: 700 }}>Click rev: {money(row.revenue)}</div>
                      <div style={{ fontSize: 11, color: Number(row.influenced_revenue || 0) > 0 ? '#1D9E75' : '#4B5563', fontWeight: 700 }}>7d: {money(row.influenced_revenue)} · {number(row.influenced_buyers)}</div>
                    </div>
                  );
                })}
                {!(feedback.trigger_emails || []).length && <div style={{ fontSize: 12, color: '#4B5563' }}>No trigger emails have been queued yet.</div>}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 10 }}>Answers by stage</div>
                {(feedback.stage_breakdown || []).map(row => {
                  const max = Math.max(...(feedback.stage_breakdown || []).map(r => Number(r.answers || 0)), 1);
                  const width = Math.max(5, (Number(row.answers || 0) / max) * 100);
                  return (
                    <div key={`${row.pyramid_stage}-${row.link_id}`} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#d4d4d4', marginBottom: 4 }}>
                        <span>{row.link_id}</span>
                        <span style={{ color: '#9CA3AF' }}>{number(row.answers)} / {number(row.contacts)} contacts</span>
                      </div>
                      <div style={{ height: 6, background: '#111', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${width}%`, height: '100%', background: row.pyramid_stage === 'visualize' ? '#378ADD' : '#8B5CF6', borderRadius: 999 }} />
                      </div>
                    </div>
                  );
                })}
                {!(feedback.stage_breakdown || []).length && <div style={{ fontSize: 12, color: '#4B5563' }}>No answer clicks yet.</div>}
              </div>

              <div style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 10 }}>7-day conversion by answer</div>
                {(feedback.conversion_by_answer || []).map(row => {
                  const max = Math.max(...(feedback.conversion_by_answer || []).map(r => Number(r.answered || 0)), 1);
                  const width = Math.max(5, (Number(row.answered || 0) / max) * 100);
                  return (
                    <div key={row.link_id} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: '#d4d4d4', marginBottom: 4 }}>
                        <span>{row.link_id}</span>
                        <span style={{ color: Number(row.bought || 0) > 0 ? '#1D9E75' : '#9CA3AF' }}>{number(row.bought)} / {number(row.answered)} bought · {pct(row.conv_pct_7d ?? row.conv_pct)} · {money(row.revenue_7d)}</span>
                      </div>
                      <div style={{ height: 6, background: '#111', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${width}%`, height: '100%', background: '#1D9E75', borderRadius: 999 }} />
                      </div>
                    </div>
                  );
                })}
                {!(feedback.conversion_by_answer || []).length && <div style={{ fontSize: 12, color: '#4B5563' }}>No conversion-by-answer data yet.</div>}
              </div>
            </div>
          </div>
        )}

        {isExpanded && abandonment && (
          <div onClick={e => e.stopPropagation()} style={{ borderTop: '0.5px solid #2a2a2a', padding: '14px 20px', cursor: 'default' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#4B5563' }}>
                CASE STUDY CHECKOUT ABANDONMENT
              </div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>45-minute recovery · 7-day attribution window</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10, marginBottom: 12 }}>
              {[
                ['CHECKOUT STARTS', number(abandonment.checkout_starts), '#fff'],
                ['ABANDONED', number(abandonment.abandoned), '#f59e0b'],
                ['EMAILS SENT', number(abandonment.emails_sent), '#378ADD'],
                ['EMAIL CLICKS', number(abandonment.emails_clicked), '#378ADD'],
                ['DIRECT RECOVERY', `${number(abandonment.direct_recoveries)} / ${money(abandonment.direct_revenue)}`, '#1D9E75'],
                ['7D INFLUENCE', `${number(abandonment.influenced_recoveries)} / ${money(abandonment.influenced_revenue)}`, '#1D9E75'],
              ].map(([label, value, color]) => (
                <div key={label} style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
              {[
                ['PENDING TIMERS', number(abandonment.pending), '#f59e0b'],
                ['CHECKOUT CONVERSIONS', number(abandonment.checkout_conversions), '#1D9E75'],
                ['FAILED-PAYMENT SUPPRESSIONS', number(abandonment.failed_payment_suppressed), '#ef4444'],
              ].map(([label, value, color]) => (
                <div key={label} style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: '9px 10px' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color }}>{value}</div>
                </div>
              ))}
            </div>
            {abandonment.breakdown?.length > 0 && (
              <div style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280', marginBottom: 8 }}>
                  Filterable dimensions: date · lead source · UTM content · device · payment method · direct vs. influenced · $29 checkout path
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {abandonment.breakdown.slice(0, 8).map((row, idx) => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr .8fr .8fr .7fr .7fr .9fr', gap: 8, fontSize: 11, color: '#9CA3AF' }}>
                      <span>{row.lead_source}</span>
                      <span>{row.utm_content}</span>
                      <span>{row.device_type}</span>
                      <span>{row.payment_method}</span>
                      <span>{number(row.checkout_starts)} starts</span>
                      <span>{number(row.direct_recoveries)} direct</span>
                      <span>{money(row.recovered_revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {isExpanded && isMemberCompletion && (
          <div onClick={e => e.stopPropagation()} style={{ borderTop: '0.5px solid #2a2a2a', padding: '14px 20px', cursor: 'default' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#4B5563' }}>
                MEMBER COMPLETION LEADS
              </div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>
                Showing the latest {number(recentActivity.length)} members who started the funnel
              </div>
            </div>

            <div style={{ background: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: 8, overflow: 'hidden' }}>
              {recentActivity.map(row => (
                <div
                  key={row.email}
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) 92px 98px 104px minmax(0, 1.2fr)', gap: 10, alignItems: 'center', padding: '10px 12px', borderBottom: '0.5px solid #2a2a2a' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#fff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.first_name || row.email}</div>
                    {row.first_name && <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.email}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: '#378ADD' }}>Done: {number(row.completions)}</div>
                  <div style={{ fontSize: 11, color: '#1D9E75' }}>Sent: {number(row.emails_sent)}</div>
                  <div style={{ fontSize: 11, color: Number(row.errors || 0) > 0 ? '#EF4444' : '#6B7280' }}>Errors: {number(row.errors)}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={(row.completed_units || []).join(', ')}>
                    {shortDate(row.last_completed_at)} · {(row.completed_units || []).slice(-2).join(', ') || '—'}
                  </div>
                </div>
              ))}
              {!recentActivity.length && <div style={{ fontSize: 12, color: '#4B5563', padding: 12 }}>No member completion events have been recorded yet.</div>}
            </div>
          </div>
        )}

        {isExpanded && <div onClick={e => e.stopPropagation()} style={{ borderTop: '0.5px solid #2a2a2a', padding: '14px 20px', cursor: 'default' }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#4B5563', marginBottom: 10 }}>
            EMAILS — click to preview
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {emails.map((email, idx) => (
              <div
                key={email.id}
                onClick={() => setModal(idx)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: usesEnrollmentStats ? '90px minmax(0, 1fr) auto auto 18px' : 'minmax(0, 1fr) auto auto 18px',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  background: '#1a1a1a',
                  border: '0.5px solid #2a2a2a',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#1f1f1f'}
                onMouseLeave={e => e.currentTarget.style.background = '#1a1a1a'}
              >
                {usesEnrollmentStats && <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600 }}>{email.delay_label}</span>}
                <span style={{ fontSize: 13, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{email.subject}</span>
                <span style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>
                  Sent: {number(email.sent)} · Opens: {number(email.opens)} · Clicks: {number(email.clicks)}
                </span>
                <span style={{ fontSize: 11, color: isMemberCompletion ? (Number(email.errors || 0) > 0 ? '#EF4444' : '#378ADD') : (Number(email.attributed_revenue || 0) > 0 ? '#1D9E75' : '#4B5563'), whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {isMemberCompletion
                    ? `Completions: ${number(email.completions)} · Errors: ${number(email.errors)}`
                    : `Revenue: ${money(email.attributed_revenue)} · Buyers: ${number(email.attributed_buyers)}`}
                </span>
                <span style={{ fontSize: 14, color: '#4B5563' }}>👁</span>
              </div>
            ))}
            {!emails.length && (
              <div style={{ fontSize: 13, color: '#4B5563', padding: '10px 0' }}>No emails configured yet.</div>
            )}
          </div>
        </div>}
      </div>

      {/* Modal */}
      {modal !== null && (
        <EmailPreviewModal
          funnel={funnel}
          emails={emails}
          initialIndex={modal}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

// ─── Main Funnels Page ────────────────────────────────────────────────────────
export default function Funnels() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [expandedFunnelId, setExpandedFunnelId] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadFunnels = useCallback(() => {
    return get('/api/funnels')
      .then(setData)
      .catch(err => setError(err.message || 'Failed to load funnels'))
      .finally(() => {
        setLoading(false);
        setLastUpdated(new Date());
      });
  }, []);

  useEffect(() => {
    loadFunnels();
  }, [loadFunnels]);

  useEffect(() => {
    if (typeof EventSource === 'undefined') return undefined;
    const streamUrl = process.env.NODE_ENV === 'production' ? '/api/funnels/events' : 'http://localhost:3000/api/funnels/events';
    const source = new EventSource(streamUrl);
    const refresh = (event) => {
      try {
        const payload = JSON.parse(event.data || '{}');
        if (payload.type === 'funnels:heartbeat' || payload.type === 'funnels:connected') return;
      } catch (_) {}
      loadFunnels();
    };
    source.addEventListener('funnels:update', refresh);
    source.onerror = () => {
      // Browser will retry automatically; polling below remains a fallback.
    };
    return () => source.close();
  }, [loadFunnels]);

  useEffect(() => {
    const id = setInterval(() => loadFunnels(), 15000);
    return () => clearInterval(id);
  }, [loadFunnels]);

  const handleToggle = async (funnel) => {
    if (toggling) return;
    setToggling(true);
    try {
      const result = await post(`/api/funnels/${funnel.id}/pause`);
      // Update local state
      setData(prev => ({
        ...prev,
        funnels: prev.funnels.map(f => f.id === funnel.id ? { ...f, status: result.status } : f),
      }));
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally {
      setToggling(false);
    }
  };

  if (loading) return <div style={{ color: '#6B7280', padding: 40 }}>Loading funnels…</div>;
  if (error) return <div style={{ color: '#EF4444', padding: 40 }}>Error: {error}</div>;

  const aggregate = data?.aggregate || {};
  const funnels = data?.funnels || [];
  const aggregateStats = funnels.reduce((acc, funnel) => {
    const stats = funnel.stats || {};
    const usesEnrollmentStats = funnel.type === 'sequence' || funnel.type === 'member_completion';
    acc.funnels += funnel.status === 'active' || funnel.status === 'paused' ? 1 : 0;
    acc.total_enrolled += Number(usesEnrollmentStats ? stats.enrolled : stats.opt_ins || 0);
    acc.emails_sent += Number(usesEnrollmentStats ? stats.sent : stats.recipients || 0);
    acc.revenue += Number(stats.revenue || 0);
    return acc;
  }, { funnels: 0, total_enrolled: 0, emails_sent: 0, revenue: 0 });
  const summary = funnels.length ? aggregateStats : aggregate;
  const summaryRevenuePerLead = revenuePerLead(summary.revenue, summary.total_enrolled);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page header */}
      <div>
        <div style={{ color: '#10b981', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.12em' }}>FUNNEL DASHBOARD</div>
        <h1 style={{ margin: '6px 0 4px', fontSize: 22, fontWeight: 500, color: '#fff', letterSpacing: '-0.02em' }}>Funnels</h1>
        <p style={{ margin: 0, fontSize: 13, color: '#6B7280' }}>All email funnels — drip sequences and conversion funnels in one place. Revenue uses the same 30-day landing-page attribution as Tracking. Feedback updates live as clicks, triggers, and purchases come in.</p>
        <div style={{ marginTop: 6, fontSize: 11, color: '#4B5563' }}>
          Live refresh enabled{lastUpdated ? ` · Last updated ${lastUpdated.toLocaleTimeString()}` : ''}
        </div>
      </div>

      {/* Aggregate stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {[
          { label: 'FUNNELS', value: number(summary.funnels), color: '#fff' },
          { label: 'TOTAL ENROLLED', value: number(summary.total_enrolled), color: '#fff' },
          { label: 'EMAILS SENT', value: number(summary.emails_sent), color: '#fff' },
          { label: 'REVENUE', value: money(summary.revenue), color: '#1D9E75' },
          { label: 'REVENUE / LEAD', value: money(summaryRevenuePerLead), color: '#1D9E75' },
        ].map(stat => (
          <div key={stat.label} style={{ background: '#141414', border: '0.5px solid #2a2a2a', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6B7280' }}>{stat.label}</div>
            <div style={{ marginTop: 6, fontSize: 22, fontWeight: 500, color: stat.color }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Funnel cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {funnels.map(funnel => (
          <FunnelCard
            key={funnel.id}
            funnel={funnel}
            onToggle={handleToggle}
            toggling={toggling}
            isExpanded={expandedFunnelId === funnel.id}
            onExpand={() => setExpandedFunnelId(current => current === funnel.id ? null : funnel.id)}
          />
        ))}
        {!funnels.length && (
          <div style={{ color: '#6B7280', fontSize: 14, padding: 20 }}>No funnels configured.</div>
        )}
      </div>
    </div>
  );
}
